import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const RETENTION_LEVELS = {
  NEW: { days: 1, label: 'New', color: 'gray' },
  LEARNING: { days: 3, label: 'Learning', color: 'yellow' },
  INTERMEDIATE: { days: 7, label: 'Intermediate', color: 'blue' },
  ADVANCED: { days: 14, label: 'Advanced', color: 'purple' },
  MASTERED: { days: 30, label: 'Mastered', color: 'green' }
};

const QUALITY_LEVELS = {
  AGAIN: { 
    value: 0, 
    label: 'Need to Relearn',
    feedback: 'Let\'s strengthen your understanding of this topic.',
    suggestion: 'Review the basics and try some practice exercises.',
    nextReviewDays: 1
  },
  HARD: { 
    value: 0.3, 
    label: 'Hard',
    feedback: 'You\'re making progress, but this topic needs more practice.',
    suggestion: 'Focus on the challenging parts and review related concepts.',
    nextReviewDays: 3
  },
  GOOD: { 
    value: 0.6, 
    label: 'Good',
    feedback: 'Solid understanding! Keep up the good work.',
    suggestion: 'Try applying this knowledge in different contexts.',
    nextReviewDays: 7
  },
  EASY: { 
    value: 1, 
    label: 'Easy',
    feedback: 'Excellent! You\'ve mastered this topic.',
    suggestion: 'Consider teaching others or exploring advanced applications.',
    nextReviewDays: 14
  }
};

export default function SkillRetention({ skill, onUpdate }) {
  const [needsReview, setNeedsReview] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentReview, setCurrentReview] = useState(null);
  const [reviewStep, setReviewStep] = useState('quality');
  const [selectedQuality, setSelectedQuality] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (skill?.subtopics) {
      const now = new Date();
      const topicsNeedingReview = skill.subtopics.filter(subtopic => {
        if (!subtopic.lastReviewed) return true;
        const lastReview = new Date(subtopic.lastReviewed.toDate());
        const daysSinceReview = Math.floor((now - lastReview) / (1000 * 60 * 60 * 24));
        const nextReview = calculateNextReview(subtopic);
        return daysSinceReview >= nextReview;
      });
      setNeedsReview(topicsNeedingReview);
    }
  }, [skill]);

  const calculateNextReview = (subtopic) => {
    const history = subtopic.reviewHistory || [];
    if (history.length === 0) return RETENTION_LEVELS.NEW.days;

    const lastReview = history[history.length - 1];
    const quality = lastReview.quality || 0;
    const currentInterval = lastReview.interval || 1;

    // SuperMemo-2 algorithm with quality-based adjustment
    const newInterval = Math.max(1, Math.round(currentInterval * (1 + quality)));
    return Math.min(newInterval, RETENTION_LEVELS[subtopic.retentionLevel || 'NEW'].days);
  };

  const calculateEffectiveness = (subtopic) => {
    const history = subtopic.reviewHistory || [];
    if (history.length === 0) return 0;

    const recentReviews = history.slice(-5);
    const averageQuality = recentReviews.reduce((sum, review) => sum + (review.quality || 0), 0) / recentReviews.length;
    const consistency = recentReviews.filter(review => review.quality >= 0.6).length / recentReviews.length;
    const timeConsistency = calculateTimeConsistency(recentReviews);

    return (averageQuality + consistency + timeConsistency) / 3;
  };

  const calculateTimeConsistency = (reviews) => {
    if (reviews.length < 2) return 0;
    
    const intervals = [];
    for (let i = 1; i < reviews.length; i++) {
      const current = new Date(reviews[i].timestamp.toDate());
      const previous = new Date(reviews[i - 1].timestamp.toDate());
      const days = Math.floor((current - previous) / (1000 * 60 * 60 * 24));
      intervals.push(days);
    }

    const averageInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    const variance = intervals.reduce((sum, interval) => sum + Math.pow(interval - averageInterval, 2), 0) / intervals.length;
    
    // Lower variance means more consistent review intervals
    return Math.max(0, 1 - (variance / 100));
  };

  const handleReview = async (subtopic, quality) => {
    try {
      setSelectedQuality(quality);
      setReviewStep('feedback');
      setError(null);
    } catch (error) {
      console.error('Error in review selection:', error);
      setError('Failed to process review selection. Please try again.');
    }
  };

  const handleCompleteReview = async () => {
    if (!currentReview || !selectedQuality) {
      setError('Please complete all review steps.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      console.log('Starting review update...', {
        skillId: skill.id,
        subtopicId: currentReview.id,
        quality: selectedQuality
      });

      const skillRef = doc(db, 'skills', skill.id);
      
      // Create the review data with default values to prevent undefined
      const reviewData = {
        timestamp: Timestamp.now(),
        subtopicId: currentReview.id || '',
        quality: QUALITY_LEVELS[selectedQuality]?.value || 0,
        interval: QUALITY_LEVELS[selectedQuality]?.nextReviewDays || 1,
        previousLevel: currentReview.retentionLevel || 'NEW',
        newLevel: determineNewLevel(currentReview, selectedQuality) || 'NEW',
        notes: reviewNotes || '',
        feedback: QUALITY_LEVELS[selectedQuality]?.feedback || '',
        suggestion: QUALITY_LEVELS[selectedQuality]?.suggestion || ''
      };

      console.log('Review data created:', reviewData);

      // Get the current skill data to ensure we have the latest state
      const skillDoc = await getDoc(skillRef);
      const currentSkillData = skillDoc.data();

      // Update the subtopic in the skills array with null checks
      const updatedSubtopics = (currentSkillData.subtopics || []).map(subtopic => {
        if (subtopic.id === currentReview.id) {
          const updatedSubtopic = {
            ...subtopic,
            lastReviewed: Timestamp.now(),
            retentionLevel: reviewData.newLevel,
            reviewHistory: [...(subtopic.reviewHistory || []), reviewData]
          };
          console.log('Updated subtopic:', updatedSubtopic);
          return updatedSubtopic;
        }
        return subtopic;
      });

      // Ensure we have valid data before updating
      if (!updatedSubtopics || updatedSubtopics.length === 0) {
        throw new Error('No valid subtopics to update');
      }

      console.log('Updating Firestore with subtopics:', updatedSubtopics);

      // Update Firestore with validated data
      await updateDoc(skillRef, {
        subtopics: updatedSubtopics
      });

      console.log('Firestore update successful');

      // Update local state
      setReviewStep('complete');
      
      // Update the needsReview state
      const now = new Date();
      const topicsNeedingReview = updatedSubtopics.filter(subtopic => {
        if (!subtopic.lastReviewed) return true;
        const lastReview = new Date(subtopic.lastReviewed.toDate());
        const daysSinceReview = Math.floor((now - lastReview) / (1000 * 60 * 60 * 24));
        const nextReview = calculateNextReview(subtopic);
        return daysSinceReview >= nextReview;
      });
      setNeedsReview(topicsNeedingReview);

      // Update the skill prop with the new data without triggering edit modal
      const updatedSkill = {
        ...skill,
        subtopics: updatedSubtopics
      };
      
      // Call onUpdate with a flag to indicate this is a review update
      onUpdate(updatedSkill, { isReviewUpdate: true });
      
      // Reset after success
      setTimeout(() => {
        setShowReviewModal(false);
        setCurrentReview(null);
        setReviewStep('quality');
        setSelectedQuality(null);
        setReviewNotes('');
        setIsSubmitting(false);
      }, 2000);

    } catch (error) {
      console.error('Error updating review:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setError(`Failed to save review: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const determineNewLevel = (subtopic, quality) => {
    const currentLevel = subtopic.retentionLevel || 'NEW';
    const effectiveness = calculateEffectiveness(subtopic);
    const levels = Object.keys(RETENTION_LEVELS);
    const currentIndex = levels.indexOf(currentLevel);

    if (quality === 'AGAIN') return 'NEW';
    if (quality === 'EASY' && effectiveness > 0.8) {
      return levels[Math.min(currentIndex + 1, levels.length - 1)];
    }
    if (quality === 'HARD' && effectiveness < 0.4) {
      return levels[Math.max(currentIndex - 1, 0)];
    }
    return currentLevel;
  };

  const calculateNextReviewDate = (subtopic) => {
    if (!subtopic.lastReviewed) return new Date();
    const lastReview = new Date(subtopic.lastReviewed.toDate());
    const nextReviewDays = calculateNextReview(subtopic);
    const nextReviewDate = new Date(lastReview);
    nextReviewDate.setDate(nextReviewDate.getDate() + nextReviewDays);
    return nextReviewDate;
  };

  const formatTimeUntilReview = (nextReviewDate) => {
    const now = new Date();
    const diffTime = nextReviewDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Ready for review';
    if (diffDays === 1) return 'Review tomorrow';
    if (diffDays < 7) return `Review in ${diffDays} days`;
    if (diffDays < 30) return `Review in ${Math.ceil(diffDays / 7)} weeks`;
    return `Review in ${Math.ceil(diffDays / 30)} months`;
  };

  const renderReviewModal = () => {
    switch (reviewStep) {
      case 'quality':
        return (
          <>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
              Review: {currentReview.name}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              How well do you remember this topic?
            </p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(QUALITY_LEVELS).map(([key, { label }]) => (
                <button
                  key={key}
                  onClick={() => handleReview(currentReview, key)}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        );

      case 'feedback':
        return (
          <>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
              Review Notes
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  {QUALITY_LEVELS[selectedQuality].feedback}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Suggestion: {QUALITY_LEVELS[selectedQuality].suggestion}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Add your notes (optional)
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                  rows={3}
                  placeholder="What did you learn? What was challenging?"
                />
              </div>
              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setReviewStep('quality')}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Back
                </button>
                <button
                  onClick={handleCompleteReview}
                  className={`px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Complete Review'}
                </button>
              </div>
            </div>
          </>
        );

      case 'complete':
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
              Review Completed!
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Your progress has been saved.
            </p>
          </div>
        );
    }
  };

  const renderReviewHistory = (subtopic) => {
    const history = subtopic.reviewHistory || [];
    if (history.length === 0) return null;

    return (
      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Review History ({history.length} reviews)
        </h4>
        <div className="space-y-2">
          {history.slice().reverse().map((review, index) => (
            <div 
              key={index}
              className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 space-y-2"
            >
              <div className="flex justify-between items-start">
                <div>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium
                    ${review.quality >= 0.8 ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' :
                      review.quality >= 0.5 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200' :
                      review.quality >= 0.3 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200'}`}
                  >
                    {review.quality >= 0.8 ? 'Easy' :
                     review.quality >= 0.5 ? 'Good' :
                     review.quality >= 0.3 ? 'Hard' : 'Need to Relearn'}
                  </span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    {review.timestamp.toDate().toLocaleDateString()}
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full
                  ${review.newLevel === 'MASTERED' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' :
                    review.newLevel === 'ADVANCED' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200' :
                    review.newLevel === 'INTERMEDIATE' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200' :
                    review.newLevel === 'LEARNING' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200'}`}
                >
                  {review.newLevel}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{review.feedback}</p>
              {review.notes && (
                <p className="text-sm text-gray-500 dark:text-gray-500 italic">
                  Notes: {review.notes}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Next review: {review.interval} days
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4 relative">
        {needsReview.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg"
          >
            <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Topics Needing Review
            </h3>
            <div className="space-y-2">
              {needsReview.map(subtopic => {
                const nextReviewDate = calculateNextReviewDate(subtopic);
                return (
                  <div
                    key={subtopic.id}
                    className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm"
                  >
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{subtopic.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Last reviewed: {subtopic.lastReviewed ? new Date(subtopic.lastReviewed.toDate()).toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTimeUntilReview(nextReviewDate)}
                      </span>
                      <button
                        onClick={() => {
                          setCurrentReview(subtopic);
                          setShowReviewModal(true);
                        }}
                        className="px-3 py-1 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        Review Now
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Learning Progress
          </h3>
          <div className="space-y-4">
            {skill?.subtopics?.map(subtopic => {
              const effectiveness = calculateEffectiveness(subtopic);
              const level = RETENTION_LEVELS[subtopic.retentionLevel || 'NEW'];
              const nextReviewDate = calculateNextReviewDate(subtopic);
              const needsReview = !subtopic.lastReviewed || new Date() >= nextReviewDate;
              const reviewCount = subtopic.reviewHistory?.length || 0;
              
              return (
                <div key={subtopic.id} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-300">{subtopic.name}</span>
                    <div className="flex items-center gap-2">
                      {needsReview ? (
                        <button
                          onClick={() => {
                            setCurrentReview(subtopic);
                            setShowReviewModal(true);
                          }}
                          className="px-2 py-1 text-xs bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                        >
                          Review Now
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimeUntilReview(nextReviewDate)}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-sm bg-${level.color}-100 text-${level.color}-800 dark:bg-${level.color}-900/20 dark:text-${level.color}-200`}>
                        {level.label}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-${level.color}-500`}
                        style={{ width: `${effectiveness * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Learning Effectiveness: {Math.round(effectiveness * 100)}%</span>
                      <span>Reviews: {reviewCount}</span>
                    </div>
                  </div>
                  {renderReviewHistory(subtopic)}
                </div>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {showReviewModal && currentReview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
              onClick={() => setShowReviewModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                {renderReviewModal()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
} 